import React, { useState, useEffect, useCallback } from 'react';
import { Printer } from 'lucide-react';

const DuesReportView = ({ showToast, currentUser }) => {
  const [reportData, setReportData] = useState([]);
  const [uniqueDuesNames, setUniqueDuesNames] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [selectedBlockId, setSelectedBlockId] = useState('');
  const [selectedResponsiblePartyRole, setSelectedResponsiblePartyRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [buildingName, setBuildingName] = useState('');

  // Load initial data (blocks, building name)
  const loadInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [blocksData, buildingsData] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blocks`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }).then(res => res.json()),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/api/buildings`, { // Fetch all buildings to find the current user's building name
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }).then(res => res.json()),
      ]);
      setBlocks(blocksData || []);
      if (currentUser && currentUser.buildingId && buildingsData) {
        const currentBuilding = buildingsData.find(b => b.id === currentUser.buildingId);
        if (currentBuilding) {
          setBuildingName(currentBuilding.name);
        }
      }
    } catch (error) {
      showToast("Başlangıç verileri yüklenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Load report data based on filters
  const loadReportData = useCallback(async () => {
    if (!selectedPeriod) {
      setReportData([]);
      setUniqueDuesNames([]);
      return;
    }

    setIsLoading(true);
    try {
      const filters = { period: selectedPeriod };
      if (selectedBlockId) filters.blockId = selectedBlockId;
      if (selectedResponsiblePartyRole) filters.responsiblePartyRole = selectedResponsiblePartyRole;

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/dues-report-summary?` + new URLSearchParams(filters), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('currentUser');
          window.location.reload();
          throw new Error('Oturum süresi doldu veya yetkiniz yok.');
        }
        throw new Error('Rapor verileri yüklenemedi.');
      }
      const data = await response.json();
      setReportData(data.reportData || []);
      setUniqueDuesNames(data.uniqueDuesNames || []);
    } catch (error) {
      showToast("Rapor verileri yüklenirken hata: " + error.message, "error");
      setReportData([]);
      setUniqueDuesNames([]);
    } finally {
      setIsLoading(false);
    }
  }, [showToast, selectedPeriod, selectedBlockId, selectedResponsiblePartyRole, currentUser]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  const handlePrint = () => {
    const printContent = document.querySelector('.print-area').innerHTML;
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write('<html><head><title>Aidat Raporu</title>');
    // Copy styles from the main document or link external stylesheets
    const stylesheets = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
      .map(link => `<link rel="stylesheet" href="${link.href}">`)
      .join('');
    printWindow.document.write(stylesheets);
    printWindow.document.write('<style>');
    printWindow.document.write(`
      body { font-family: sans-serif; margin: 20px; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
      .report-header { text-align: center; margin-bottom: 20px; }
      .no-print { display: none !important; } /* Hide elements not meant for print */
    `);
    printWindow.document.write('</style></head><body>');
    printWindow.document.write(printContent);
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const blocksForFilter = currentUser && currentUser.buildingId && blocks.length > 0
    ? blocks.filter(block => String(block.buildingId) === String(currentUser.buildingId))
    : [];

  return (
    <div className="bg-white p-6 rounded-lg shadow print-area">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-blue-700">Rapor oluşturuluyor...</p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6 no-print">
        <h1 className="text-2xl font-semibold">Aidat Raporu</h1>
        <button
          onClick={handlePrint}
          className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center disabled:opacity-50"
          disabled={isLoading || reportData.length === 0}
        >
          <Printer size={18} className="mr-2" /> Yazdır
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6 p-4 border rounded-lg bg-gray-50 no-print">
        <div>
          <label htmlFor="reportPeriodSelect" className="label-form-style">Dönem Seçin (YYYY-AA):</label>
          <input
            type="month"
            id="reportPeriodSelect"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="input-form-style"
          />
        </div>
        <div>
          <label htmlFor="reportBlockFilter" className="label-form-style">Bloğa Göre Filtrele (Opsiyonel):</label>
          <select
            id="reportBlockFilter"
            value={selectedBlockId}
            onChange={(e) => setSelectedBlockId(e.target.value)}
            className="input-form-style"
            disabled={blocksForFilter.length === 0}
          >
            <option value="">Tüm Bloklar</option>
            {blocksForFilter.map(bl => <option key={bl.id} value={bl.id}>{bl.name}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="reportResponsiblePartyRole" className="label-form-style">Sorumlu Rolü (Opsiyonel):</label>
          <select
            id="reportResponsiblePartyRole"
            value={selectedResponsiblePartyRole}
            onChange={(e) => setSelectedResponsiblePartyRole(e.target.value)}
            className="input-form-style"
          >
            <option value="">Tüm Roller</option>
            <option value="residentOrDefaultOwner">Sakin (Yoksa Kat Maliki)</option>
            <option value="ownerOnly">Sadece Kat Maliki</option>
          </select>
        </div>
      </div>

      {selectedPeriod && reportData.length > 0 && (
        <div className="report-header text-center mb-6">
          <h2 className="text-xl font-bold">{buildingName} Aidat Raporu</h2>
          <p className="text-lg">Dönem: {selectedPeriod}</p>
        </div>
      )}

      {selectedPeriod && reportData.length === 0 && !isLoading ? (
        <p className="text-gray-500 text-center py-4">Seçilen dönem ve filtrelere göre rapor verisi bulunamadı.</p>
      ) : null}

      {reportData.length > 0 ? (
        <div className="overflow-x-auto shadow border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="th-style">Daire No</th>
                <th className="th-style">Blok</th>
                <th className="th-style">Arsa Payı</th>
                <th className="th-style">Önceki Borç</th>
                {uniqueDuesNames.map(name => (
                  <th key={name} className="th-style">{name}</th>
                ))}
                <th className="th-style">Bu Ay Toplam</th>
                <th className="th-style">Genel Toplam</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.map(summary => (
                <tr key={summary.summaryId}> {/* Use summaryId as key */}
                  <td className="td-style font-medium">
                    {summary.personName}
                    <span className="block text-xs text-gray-500">({summary.responsiblePartyType === 'owner' ? 'Kat Maliki' : 'Sakin'})</span>
                  </td>
                  <td className="td-style">
                    {summary.apartmentNumber ? `D: ${summary.apartmentNumber}` : '-'}
                    <span className="block text-xs text-gray-500">{summary.blockName ? `(${summary.blockName})` : ''}</span>
                  </td>
                  <td className="td-style">{summary.arsaPayi ? summary.arsaPayi : '-'}</td>
                  <td className="td-style text-red-600 font-semibold">{summary.previousDebt.toFixed(2)} TL</td>
                  {uniqueDuesNames.map(name => (
                    <td key={`${summary.summaryId}-${name}`} className="td-style">
                      {(summary.currentDuesBreakdown[name] || 0).toFixed(2)} TL
                    </td>
                  ))}
                  <td className="td-style font-semibold">{summary.totalCurrentMonthDue.toFixed(2)} TL</td>
                  <td className="td-style font-bold text-blue-700">{summary.totalOverallDue.toFixed(2)} TL</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <style jsx>{`
        .label-form-style { display: block; margin-bottom: 0.25rem; font-size: 0.875rem; font-weight: 500; color: #374151; }
        .input-form-style { width: 100%; padding: 0.5rem; border: 1px solid #D1D5DB; border-radius: 0.375rem; box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); }
        .btn-primary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: white; background-color: #2563EB; border-radius: 0.375rem; }
        .btn-primary-style:hover { background-color: #1D4ED8; }
        .btn-secondary-style { padding: 0.5rem 1rem; font-size: 0.875rem; color: #374151; background-color: #E5E7EB; border-radius: 0.375rem; }
        .btn-secondary-style:hover { background-color: #D1D5DB; }
        .th-style { padding: 0.75rem 1.5rem; text-align: center; font-size: 0.75rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; }
        .td-style { padding: 0.75rem 1.5rem; white-space: nowrap; font-size: 0.875rem; color: #374151; text-align: center; }

        @media print {
          .no-print {
            display: none !important;
          }
          .print-area {
            box-shadow: none;
            padding: 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
          }
          th, td {
            border: 1px solid #ccc;
            padding: 8px;
            font-size: 10px; /* Smaller font for print */
            white-space: normal; /* Allow text to wrap */
            text-align: center !important; /* Center align all table cells for print */
          }
          .report-header {
            margin-bottom: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default DuesReportView;
